import { useCallback, useRef, useState, type ChangeEvent, type DragEvent, type RefObject } from "react";
import type { FileRejection, FileValidator } from "./types";

export interface UseFilePickerOptions {
  /** Passed straight through to the hidden <input accept>. */
  accept?: string;
  /** Passed straight through to the hidden <input multiple>. Defaults to false. */
  multiple?: boolean;
  /** Per-file validator. Omit to admit every selected file unvalidated. */
  validate?: FileValidator;
  /** Called with the subset of selected files that passed validation. */
  onFilesAdmitted: (files: File[]) => void;
  /** Called whenever the rejection set changes — including with `[]` at the
   *  start of processing a new selection, which clears any stale error the
   *  caller may be displaying. */
  onRejected?: (rejections: FileRejection[]) => void;
  disabled?: boolean;
}

export interface UseFilePickerResult {
  inputRef: RefObject<HTMLInputElement>;
  /** Spread directly onto a hidden <input type="file">. */
  inputProps: {
    ref: RefObject<HTMLInputElement>;
    type: "file";
    accept?: string;
    multiple?: boolean;
    className: string;
    disabled?: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  /** Programmatically opens the OS file picker. */
  openFilePicker: () => void;
  isValidating: boolean;
  rejections: FileRejection[];
  isDragActive: boolean;
  /** Spread onto a dropzone container. */
  dropzoneProps: {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}

// Headless: owns the pick -> validate -> admit/reject pipeline, shared by any
// presentation (a plain trigger button, a dropzone, or something else later).
export function useFilePicker({
  accept,
  multiple = false,
  validate,
  onFilesAdmitted,
  onRejected,
  disabled,
}: UseFilePickerOptions): UseFilePickerResult {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const setRejectionsAndNotify = useCallback((next: FileRejection[]) => {
    setRejections(next);
    onRejected?.(next);
  }, [onRejected]);

  const processFiles = useCallback(async (fileList: FileList | File[] | null) => {
    if (!fileList) return;
    const files = Array.from(fileList);
    if (files.length === 0) return;

    setRejectionsAndNotify([]); // clear stale error before validating

    if (!validate) {
      onFilesAdmitted(files);
      return;
    }

    setIsValidating(true);
    const results = await Promise.all(files.map(async (file) => ({ file, result: await validate(file) })));
    setIsValidating(false);

    const admitted: File[] = [];
    const newRejections: FileRejection[] = [];
    for (const { file, result } of results) {
      if (result.valid) admitted.push(file);
      else newRejections.push({ file, message: result.message ?? "Invalid file." });
    }
    setRejectionsAndNotify(newRejections);
    if (admitted.length > 0) onFilesAdmitted(admitted);
  }, [validate, onFilesAdmitted, setRejectionsAndNotify]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // Snapshot into a plain array before clearing .value — e.target.files is
    // a live FileList tied to the input, and resetting .value empties it
    // in place, so a held reference to the FileList itself (not a copied
    // array) would already be empty by the time processFiles reads it.
    const files = e.target.files ? Array.from(e.target.files) : null;
    e.target.value = ""; // allow re-selecting the same file afterwards
    void processFiles(files);
  }, [processFiles]);

  const openFilePicker = useCallback(() => inputRef.current?.click(), []);

  const dropzoneProps = {
    onDragOver: (e: DragEvent) => { e.preventDefault(); setIsDragActive(true); },
    onDragLeave: () => setIsDragActive(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
      void processFiles(e.dataTransfer.files);
    },
  };

  return {
    inputRef,
    inputProps: {
      ref: inputRef,
      type: "file",
      accept,
      multiple,
      className: "hidden",
      disabled,
      onChange: handleChange,
    },
    openFilePicker,
    isValidating,
    rejections,
    isDragActive,
    dropzoneProps,
  };
}
